import { useEffect, useMemo, useState } from 'react';
import type { EventsPage } from '@finder/contracts';
import {
  currentViewPath,
  noteViewPath,
  resultsViewPath,
  trackEventDetailOpen,
  trackPageView,
} from '../lib/analytics.js';
import { EventDetailDialog } from './EventDetailDialog.js';
import { EventList, type ListFilters } from './EventList.js';
import { EventMap } from './EventMap.js';
import { Icon } from './Icon.js';
import { applyFilters } from './list-view.js';

/**
 * Results surface for issue #11: the existing ordered list, a map built from
 * `projectMap()`, and one in-app detail dialog. `applyFilters` drops the same
 * rows the list drops, in server order. Sort stays inside EventList and does
 * not reorder map pins.
 */
export function ResultsView({
  page,
  filters,
  origin,
  originLabel = 'Seattle Center',
  distanceVoice = 'place',
  onImpressions,
  onClearClientFilters,
}: {
  page: EventsPage;
  filters: ListFilters;
  origin: { lat: number; lng: number };
  originLabel?: string;
  distanceVoice?: 'gps' | 'place';
  onImpressions: (tokens: string[]) => void;
  onClearClientFilters?: () => void;
}): JSX.Element {
  const [view, setView] = useState<'list' | 'map'>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const visible = useMemo(() => applyFilters(page.events, filters), [page.events, filters]);
  const selected = visible.find((event) => event.canonicalId === selectedId) ?? null;

  // Initial page_view is sent from the app shell. This fires only when the
  // visitor switches List / Map, which is the SPA's view change.
  useEffect(() => {
    const path = resultsViewPath(view);
    if (path === currentViewPath()) return;
    noteViewPath(path);
    trackPageView(path);
  }, [view]);

  const openDetail = (canonicalId: string, source: 'card' | 'map') => {
    trackEventDetailOpen({ eventId: canonicalId, source });
    setSelectedId(canonicalId);
  };
  const wide = useWidePane();

  const list = (
    <>
      {visible.length > 0 ? (
        <div className="view-toggle" role="group" aria-label="Results view">
          <button type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>
            <Icon name="list" size={15} />
            List
          </button>
          <button type="button" aria-pressed={view === 'map'} onClick={() => setView('map')}>
            <Icon name="map" size={15} />
            Map
          </button>
        </div>
      ) : null}

      {view === 'map' && visible.length > 0 ? (
        <EventMap
          page={page}
          events={visible}
          origin={origin}
          originLabel={originLabel}
          distanceVoice={distanceVoice}
          selectedId={selectedId}
          onSelect={(canonicalId) => openDetail(canonicalId, 'map')}
        />
      ) : (
        <EventList
          page={page}
          filters={filters}
          onImpressions={onImpressions}
          onOpenDetail={(canonicalId) => openDetail(canonicalId, 'card')}
          selectedId={selectedId}
          distanceVoice={distanceVoice}
          {...(onClearClientFilters ? { onClearClientFilters } : {})}
        />
      )}
    </>
  );

  const detail = selected ? (
    <EventDetailDialog
      key={selected.canonicalId}
      event={selected}
      origin={origin}
      distanceVoice={distanceVoice}
      presentation={wide ? 'pane' : 'sheet'}
      onClose={() => setSelectedId(null)}
    />
  ) : (
    <p className="detail-empty">Select an event to see when, where, and the original page.</p>
  );

  if (!wide) {
    return (
      <>
        {list}
        {selected ? detail : null}
      </>
    );
  }

  return (
    <div className="stage">
      <div className="stage-list">{list}</div>
      <div className="stage-detail">{detail}</div>
    </div>
  );
}

/** Laptop two-pane starts at 960px. The phone keeps the modal sheet. */
function useWidePane(): boolean {
  const [wide, setWide] = useState(() => widePaneNow());
  useEffect(() => {
    const query = window.matchMedia('(min-width: 960px)');
    const apply = () => setWide(query.matches);
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);
  return wide;
}

function widePaneNow(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(min-width: 960px)').matches;
}
